<script>
	import { modalState, hideModal, showModal } from "@app/store/modalStore.svelte.js";
	import { voiceState } from "@app/store/voiceStore.svelte.js";
	import { toast } from '@zerodevx/svelte-toast';
	import { fade } from "svelte/transition";
	import { onMount } from "svelte";
	import { Circle } from "svelte-loading-spinners";
	import voiceLib from "@cclib/providers/textToSpeechAll/voices.js";
	import voiceUtils from "@cclib/providers/textToSpeechAll/voiceUtils.js";
	import { environment } from "@app/store/envStore.svelte.js";
	import { authState } from "@app/store/authStore.svelte.js";
	import { can } from "@app/lib/entitlements.js";
	import {
		WEB_SPEECH_PROVIDER,
		cancel,
		getWebSpeechVoiceCatalog,
		isLowQualityVoice,
		isNetworkDependentVoice,
		isWebSpeechSupported,
		speak,
	} from "@app/lib/audio/webSpeechTts.js";
	import {
		LOCAL_TTS_PROVIDER,
		getParentVoices,
		renderParentAudio,
	} from "@app/lib/audio/parentTts.js";
	/* Firebase */
	import { auth, functions } from "@app/configs/firebase.js";
	import { httpsCallable } from "firebase/functions";
	import * as m from "@app/paraglide/messages.js";

	const { voiceKey } = voiceUtils;
	const SOURCE_STORAGE_KEY = "cc-voice-manager-source";
	const CLOUD_PROVIDERS = ["assemblyai", "google", "gemini", "amazon", "microsoft"];
	const OFFLINE_SOURCE = "offline";
	const ELEVENLABS_SOURCE = "ElevenLabs";
	const ALL_CLOUD_SOURCE = "cloud";
	/* Amazon lists the same voice once per engine; the grid shows only the best one */
	const AMAZON_ENGINE_RANK = { generative: 3, "long-form": 2, neural: 1, standard: 0 };

	/* Sources shown in the left rail */
	const sourceInfo = {
		[ALL_CLOUD_SOURCE]: { icon: "bi-cloud", label: () => m.voice_manager_source_all_cloud() },
		assemblyai: { icon: "bi-mic", label: () => m.voice_manager_provider_assemblyai() },
		google: { icon: "bi-google", label: () => m.voice_manager_provider_google() },
		gemini: { icon: "bi-stars", label: () => m.voice_manager_provider_gemini() },
		amazon: { icon: "bi-amazon", label: () => m.voice_manager_provider_amazon() },
		microsoft: { icon: "bi-microsoft", label: () => m.voice_manager_provider_microsoft() },
		[ELEVENLABS_SOURCE]: { icon: "bi-soundwave", label: () => m.voice_manager_provider_eleven_labs() },
		[OFFLINE_SOURCE]: { icon: "bi-laptop", label: () => m.voice_manager_provider_offline() },
	};

	const elevenLabsCategoryLabels = {
		cloned: () => m.voice_manager_elevenlabs_category_cloned(),
		professional: () => m.voice_manager_elevenlabs_category_professional(),
		premade: () => m.voice_manager_elevenlabs_category_premade(),
		generated: () => m.voice_manager_elevenlabs_category_generated(),
	};

	let source = $state(loadSavedSource()),
		gender = $state("all"),
		search = $state(""),
		language = $state("English"),
		rendering = $state(false),
		audioPlayer,
		selectedVoice = $state(),
		previewText = $state(m.voice_manager_default_preview_text()),
		offlineVoices = $state([]),
		parentDefaultVoiceId = $state(null),
		elevenLabs = $state({ status: "loading", voices: [], message: "" });

	let webSpeechAvailable = $derived(isWebSpeechSupported());
	// When the AD license is active, the parent host (e.g. Unity) supplies the
	// offline voice catalog through PenPal instead of the browser web-speech voices.
	let useParentVoices = $derived(environment.adLicense);
	let offlineAvailable = $derived(webSpeechAvailable || useParentVoices);
	const languageNames = new Intl.DisplayNames(["en"], { type: "language" });

	/* Named keys keep the family before " - "; Language-* keys become English display names */
	function languageDisplayName(key) {
		if (key.startsWith("Language-")) {
			const locale = key.slice("Language-".length);
			let lang;
			try {
				lang = new Intl.Locale(locale).language;
			} catch {
				lang = locale.split("-")[0];
			}
			if (lang === "zh" || lang === "cmn") return "Mandarin Chinese";
			return languageNames.of(lang) || locale;
		}
		return key.split(" - ")[0];
	}

	const languageGroups = {};
	for (const key of Object.keys(voiceLib)) {
		const name = languageDisplayName(key);
		(languageGroups[name] ??= []).push(key);
	}
	const languageOptions = Object.keys(languageGroups).sort((a, b) => a.localeCompare(b));

	/* Cloud voices for the selected language (all matching regional catalog groups) */
	let cloudVoices = $derived.by(() => {
		const keys = languageGroups[language] || [];
		const voices = keys.flatMap((key) => {
			const group = voiceLib[key] || { male: [], female: [] };
			return [...group.male, ...group.female];
		});
		return dedupeCloudVoices(voices);
	});

	let sourceVoices = $derived.by(() => {
		if (source === ELEVENLABS_SOURCE) return elevenLabs.voices;
		if (source === OFFLINE_SOURCE) return offlineVoices;
		if (source === ALL_CLOUD_SOURCE) return cloudVoices;
		return cloudVoices.filter((voice) => voice.provider === source);
	});

	let visibleVoices = $derived.by(() => {
		const query = search.trim().toLowerCase();
		return sourceVoices.filter((voice) => {
			if (gender !== "all" && voice.gender !== gender) return false;
			if (!query) return true;
			return searchText(voice).includes(query);
		});
	});

	/* "All cloud" is grouped by provider; every other source is a single group */
	let voiceGroups = $derived.by(() => {
		if (source !== ALL_CLOUD_SOURCE) return [{ id: source, voices: visibleVoices }];
		return CLOUD_PROVIDERS
			.map((provider) => ({ id: provider, voices: visibleVoices.filter((voice) => voice.provider === provider) }))
			.filter((group) => group.voices.length > 0);
	});

	let sourceCounts = $derived.by(() => {
		const counts = { [ALL_CLOUD_SOURCE]: cloudVoices.length };
		for (const provider of CLOUD_PROVIDERS) counts[provider] = cloudVoices.filter((voice) => voice.provider === provider).length;
		counts[ELEVENLABS_SOURCE] = elevenLabs.voices.length;
		counts[OFFLINE_SOURCE] = offlineVoices.length;
		return counts;
	});

	/* Options → Integrations is only shown on these plans (see Options.svelte) */
	let canOpenIntegrations = $derived(environment.online && can("iconik"));
	let isCloudSource = $derived(source === ALL_CLOUD_SOURCE || CLOUD_PROVIDERS.includes(source));
	let hasLowQualityOfflineVoices = $derived(offlineVoices.some((voice) => isLowQualityVoice(voice)));

	onMount(() => {
		try {
			audioPlayer = document.getElementById("adPreviewPlayer");
		} catch (err) {
			console.log(err, err.message);
		}

		if (!environment.online) {
			source = OFFLINE_SOURCE;
		} else if (source === OFFLINE_SOURCE && !offlineAvailable) {
			source = ALL_CLOUD_SOURCE;
		}

		if (useParentVoices) {
			loadParentVoices();
		} else {
			loadOfflineVoices();
		}

		if (environment.online) {
			loadElevenLabsVoices();
		} else {
			elevenLabs = { status: "offline", voices: [], message: "" };
		}

		if (!useParentVoices && webSpeechAvailable && window.speechSynthesis) {
			window.speechSynthesis.addEventListener("voiceschanged", loadOfflineVoices);

			return () => {
				cancel();
				window.speechSynthesis.removeEventListener("voiceschanged", loadOfflineVoices);
			};
		}
	});

	function loadSavedSource() {
		try {
			const saved = localStorage.getItem(SOURCE_STORAGE_KEY);
			return saved && sourceInfo[saved] ? saved : ALL_CLOUD_SOURCE;
		} catch (err) {
			return ALL_CLOUD_SOURCE;
		}
	}

	function selectSource(id) {
		source = id;
		try {
			localStorage.setItem(SOURCE_STORAGE_KEY, id);
		} catch (err) {
			/* Remembering the source is a convenience only */
		}
	}

	function isSourceDisabled(id) {
		if (id === OFFLINE_SOURCE) return false;
		return !environment.online;
	}

	function providerLabel(provider) {
		if (provider === WEB_SPEECH_PROVIDER || provider === LOCAL_TTS_PROVIDER) return sourceInfo[OFFLINE_SOURCE].label();
		return sourceInfo[provider]?.label() || provider;
	}

	/* Collapse Amazon voices that exist on several engines down to the highest-ranked engine */
	function dedupeCloudVoices(voices) {
		const best = {};
		for (const voice of voices) {
			if (voice.provider !== "amazon") {
				best[voiceKey(voice)] = voice;
				continue;
			}
			const key = `amazon:${voice.name}`;
			const current = best[key];
			if (!current || (AMAZON_ENGINE_RANK[voice.engine] ?? -1) > (AMAZON_ENGINE_RANK[current.engine] ?? -1)) {
				best[key] = voice;
			}
		}
		return Object.values(best);
	}

	/* Voice name without the locale prefix, e.g. en-US-Chirp3-HD-Kore -> Chirp3-HD-Kore */
	function displayName(voice) {
		const prefix = voice.language ? `${voice.language}-` : "";
		if (prefix && voice.name.toLowerCase().startsWith(prefix.toLowerCase())) return voice.name.slice(prefix.length);
		return voice.name;
	}

	/* Single muted meta line under the voice name */
	function voiceMeta(voice, tier, locale) {
		const genderLabel = voice.gender === "female" ? m.voice_manager_gender_female() : m.voice_manager_gender_male();
		const capitalized = genderLabel.charAt(0).toUpperCase() + genderLabel.slice(1);
		return [capitalized, tier, locale].filter(Boolean).join(" · ");
	}

	function searchText(voice) {
		return [voice.name, voice.tier, voice.language, voice.description, ...Object.values(voice.labels || {})]
			.filter(Boolean)
			.join(" ")
			.toLowerCase();
	}

	/* Google voice family from the voice name, e.g. en-US-Chirp3-HD-Kore -> Chirp3-HD */
	function voiceTier(voice) {
		if (voice.tier) return voice.tier;
		if (voice.provider === "google") {
			const match = /-(Chirp3-HD|Chirp-HD|Neural2|Wavenet|Standard|Polyglot|News)-/.exec(voice.name);
			return match ? match[1] : null;
		}
		if (voice.provider === ELEVENLABS_SOURCE && voice.category) {
			return elevenLabsCategoryLabels[voice.category]?.() || voice.category;
		}
		return null;
	}

	function voiceLocale(voice) {
		if (voice.provider === ELEVENLABS_SOURCE) {
			const languages = [...new Set((voice.languages || []).map((lang) => lang.locale || lang.language))];
			return languages.length > 0 ? languages.slice(0, 3).join(", ") + (languages.length > 3 ? " +" + (languages.length - 3) : "") : m.voice_manager_multilingual_badge();
		}
		if (voice.provider === LOCAL_TTS_PROVIDER) return voice.sourceLanguage || null;
		return voice.language || null;
	}

	function elevenLabsDetails(voice) {
		const labels = voice.labels || {};
		return [labels.accent, labels.age, labels.use_case || labels["use case"], labels.descriptive].filter(Boolean);
	}

	function normalizeOfflineVoice(voice, fallbackGender) {
		return {
			...voice,
			provider: WEB_SPEECH_PROVIDER,
			id: voice.id || voice.voiceURI || voice.name,
			voiceURI: voice.voiceURI || voice.id || voice.name,
			name: voice.name,
			styles: voice.styles || ["default"],
			gender: voice.gender || fallbackGender,
			language: voice.lang || voice.language || "Offline",
		};
	}

	function normalizeParentGender(gender) {
		const value = typeof gender === "string" ? gender.trim().toLowerCase() : "";
		return value === "female" ? "female" : "male";
	}

	function normalizeParentVoice(voice) {
		return {
			provider: LOCAL_TTS_PROVIDER,
			id: voice.id,
			name: voice.name || voice.id,
			styles: ["default"],
			gender: normalizeParentGender(voice.gender),
			language: "Offline",
			sourceLanguage: voice.language || null,
		};
	}

	function normalizeElevenLabsVoice(voice) {
		return {
			provider: ELEVENLABS_SOURCE,
			id: voice.voice_id,
			name: voice.name,
			styles: voiceUtils.elevenLabsStyles(),
			preview_url: voice.preview_url,
			gender: voice.labels && voice.labels.gender === "female" ? "female" : "male",
			language: ELEVENLABS_SOURCE,
			category: voice.category,
			labels: voice.labels || {},
			description: voice.description || "",
			languages: voice.verified_languages || [],
		};
	}

	async function loadOfflineVoices() {
		if (!webSpeechAvailable) return;

		const catalog = await getWebSpeechVoiceCatalog({ groupByGender: true });
		offlineVoices = [
			...(catalog.male || []).map((voice) => normalizeOfflineVoice(voice, "male")),
			...(catalog.female || []).map((voice) => normalizeOfflineVoice(voice, "female")),
		];
	}

	async function loadParentVoices() {
		try {
			const res = await getParentVoices();
			if (!res || res.success === false) {
				throw new Error(res?.message || m.voice_manager_no_data_error());
			}

			const voices = Array.isArray(res.voices) ? res.voices : [];
			parentDefaultVoiceId = res.defaultVoice || null;
			offlineVoices = voices.map((voice) => normalizeParentVoice(voice));

			if (parentDefaultVoiceId) {
				selectedVoice = offlineVoices.find((voice) => voice.id === parentDefaultVoiceId) || selectedVoice;
			}
		} catch (err) {
			console.log(err, err.message);
			toast.push(m.voice_manager_render_failed_toast({ error: err.message }), {
				classes: ["toast-danger"],
			});
		}
	}

	async function loadElevenLabsVoices() {
		elevenLabs = { status: "loading", voices: [], message: "" };
		try {
			if (!auth.currentUser) {
				elevenLabs = { status: "no_key", voices: [], message: "" };
				return;
			}

			const res = await httpsCallable(functions, "v8ElevenLabsGetVoicesV2")();
			const data = res.data || {};
			elevenLabs = {
				status: data.status || "error",
				voices: (data.voices || []).map(normalizeElevenLabsVoice),
				message: data.message || "",
			};
		} catch (err) {
			console.log(err, err.message);
			elevenLabs = { status: "error", voices: [], message: err.message };
		}
	}

	function openIntegrations() {
		showModal("options", { tab: "integrations" });
	}

	function saveVoicesToLocalStorage() {
		localStorage.setItem("cc-voice-defaults", JSON.stringify(voiceState));
	}

	function isPinned(v) {
		return voiceState.some((pinned) => voiceKey(pinned) === voiceKey(v));
	}

	function togglePin(v) {
		if (isPinned(v)) {
			unPinVoice(v);
		} else {
			pinVoice(v);
		}
	}

	function pinVoice(v) {
		voiceState.push(v);
		toast.push(m.voice_manager_pin_success_toast({ name: v.name }), {
			classes: ["toast-success"],
		});

		saveVoicesToLocalStorage();
	}

	function unPinVoice(v) {
		// Remove the voice without reassigning the voiceState import
		let index = voiceState.findIndex((voice) => voiceKey(voice) === voiceKey(v));
		if (index !== -1) {
			voiceState.splice(index, 1);
		}

		toast.push(m.voice_manager_unpin_success_toast({ name: v.name }), {
			classes: ["toast-success"],
		});

		saveVoicesToLocalStorage();
	}

	async function previewVoice(v) {
		rendering = true;
		selectedVoice = v;
		const sampleText = voiceUtils.prepareTtsText(previewText || m.voice_manager_default_preview_text(), v, voiceUtils.renderModel(v));
		try {
			if (v.provider === WEB_SPEECH_PROVIDER) {
				cancel();
				if (audioPlayer) {
					audioPlayer.pause();
					audioPlayer.removeAttribute("src");
				}
				await speak(sampleText, {
					voiceURI: v.voiceURI,
					rate: 1,
					pitch: 1,
					volume: 1,
				});
			} else if (v.provider === LOCAL_TTS_PROVIDER) {
				const result = await renderParentAudio({
					text: sampleText,
					voice: v.id,
					speed: 1,
					format: "mp3",
				});

				if (!result || result.success === false || !result.url) {
					throw new Error(result?.message || m.voice_manager_no_data_error());
				}

				audioPlayer.src = result.url;
				await audioPlayer.load();
				await audioPlayer.play();
			} else if (v.preview_url) {
				audioPlayer.src = v.preview_url;
				await audioPlayer.load();
				await audioPlayer.play();
			} else {
				if (!environment.online) {
					throw new Error(m.voice_manager_offline_unavailable());
				}
				let res = await httpsCallable(functions, "v8TextToSpeechProviderV5")({
					provider: v.provider,
					language: v.language,
					voice: v.name,
					voice_id: v.id,
					engine: v.engine,
					model: voiceUtils.renderModel(v),
					stability: 0.5,
					similarity: 0.5,
					speed: 1,
					pitch: 0,
					style: "default",
					audioEncoding: "MP3",
					preview: true,
					text: sampleText,
				});
				if (res.data && res.data.audio) {
					audioPlayer.src = res.data.audio;
					await audioPlayer.load();
					await audioPlayer.play();
				} else {
					throw new Error(m.voice_manager_no_data_error());
				}
			}
		} catch (err) {
			console.log(err, err.message);
			toast.push(m.voice_manager_render_failed_toast({ error: err.message }), {
				classes: ["toast-danger"],
			});
		} finally {
			rendering = false;
		}
	}
</script>

<div
	transition:fade={{ duration: 100 }}
	class="modal {modalState.selected === 'voiceManager' ? 'show d-block' : ''}"
	role="dialog"
	tabindex="-1"
	id="VoiceManagerModal"
	aria-labelledby="voiceManagerTitle"
>
	<div class="modal-dialog modal-xl modal-dialog-centered" role="document">
		<div class="modal-content">
			<div class="modal-header">
				<h4 class="modal-title" id="voiceManagerTitle">{m.voice_manager_title()}</h4>
				<button type="button" class="btn-close" id="voiceManagerClose" aria-label={m.close()} onclick={hideModal}></button>
				<audio id="adPreviewPlayer" class="d-none" autobuffer="autobuffer" preload="metadata">
					<source src="" type="audio/wav" />
				</audio>
			</div>
			<div class="modal-body p-0">
				<div class="vm-layout">
					<!-- Sources -->
					<nav class="vm-sources border-end" aria-labelledby="voiceSourcesLabel">
						<p class="vm-section-label" id="voiceSourcesLabel">{m.voice_manager_sources_label()}</p>
						{#each [ALL_CLOUD_SOURCE, ...CLOUD_PROVIDERS] as id (id)}
							<button
								type="button"
								id="voiceSource-{id}"
								class="vm-source {id === ALL_CLOUD_SOURCE ? '' : 'vm-source-child'}"
								class:active={source === id}
								aria-pressed={source === id}
								disabled={isSourceDisabled(id)}
								onclick={() => selectSource(id)}>
								<i class="bi {sourceInfo[id].icon}" aria-hidden="true"></i>
								<span class="vm-source-name">{sourceInfo[id].label()}</span>
								<span class="vm-source-count">{sourceCounts[id]}</span>
							</button>
						{/each}

						<hr class="my-2" />

						<button
							type="button"
							id="voiceSource-{ELEVENLABS_SOURCE}"
							class="vm-source"
							class:active={source === ELEVENLABS_SOURCE}
							aria-pressed={source === ELEVENLABS_SOURCE}
							disabled={isSourceDisabled(ELEVENLABS_SOURCE)}
							onclick={() => selectSource(ELEVENLABS_SOURCE)}>
							<i class="bi {sourceInfo[ELEVENLABS_SOURCE].icon}" aria-hidden="true"></i>
							<span class="vm-source-name">{sourceInfo[ELEVENLABS_SOURCE].label()}</span>
							{#if elevenLabs.status === "loading"}
								<span class="vm-source-count"><Circle size="12" color="currentColor" unit="px" duration="1s" /></span>
							{:else if elevenLabs.status === "ok"}
								<span class="vm-source-count">{sourceCounts[ELEVENLABS_SOURCE]}</span>
							{:else if elevenLabs.status === "no_key"}
								<span class="badge rounded-pill text-bg-primary">{m.voice_manager_elevenlabs_connect_short()}</span>
							{:else if elevenLabs.status !== "offline"}
								<i class="bi bi-exclamation-triangle-fill text-warning" aria-label={m.voice_manager_elevenlabs_error_short()}></i>
							{/if}
						</button>

						{#if offlineAvailable}
							<button
								type="button"
								id="voiceSource-{OFFLINE_SOURCE}"
								class="vm-source"
								class:active={source === OFFLINE_SOURCE}
								aria-pressed={source === OFFLINE_SOURCE}
								onclick={() => selectSource(OFFLINE_SOURCE)}>
								<i class="bi {sourceInfo[OFFLINE_SOURCE].icon}" aria-hidden="true"></i>
								<span class="vm-source-name">{sourceInfo[OFFLINE_SOURCE].label()}</span>
								<span class="vm-source-count">{sourceCounts[OFFLINE_SOURCE]}</span>
							</button>
						{/if}
					</nav>

					<!-- Voice browser -->
					<section class="vm-browser" aria-labelledby="voiceBrowserTitle">
						<div class="vm-toolbar border-bottom">
							<div class="d-flex flex-wrap align-items-center gap-2">
								<h5 class="m-0 me-auto" id="voiceBrowserTitle">
									<i class="bi {sourceInfo[source].icon} me-1" aria-hidden="true"></i>{sourceInfo[source].label()}
								</h5>
								<div class="vm-search">
									<i class="bi bi-search vm-search-icon" aria-hidden="true"></i>
									<input
										type="search"
										id="voiceSearchInput"
										class="form-control form-control-sm"
										placeholder={m.voice_manager_search_placeholder()}
										aria-label={m.voice_manager_search_label()}
										bind:value={search} />
								</div>
								{#if isCloudSource}
									<select class="form-select form-select-sm vm-language" id="LanguageSelectDropdown" aria-label={m.voice_manager_language_label()} bind:value={language}>
										{#each languageOptions as lang (lang)}
											<option>{lang}</option>
										{/each}
									</select>
								{/if}
								<div class="vm-segmented" role="group" aria-label={m.voice_manager_gender_label()}>
									{#each [["all", m.all()], ["male", m.voice_manager_gender_male()], ["female", m.voice_manager_gender_female()]] as [value, label] (value)}
										<button
											type="button"
											id="voiceGender-{value}"
											class="vm-segment text-capitalize"
											class:active={gender === value}
											aria-pressed={gender === value}
											onclick={() => (gender = value)}>{label}</button>
									{/each}
								</div>
							</div>
							<div class="d-flex align-items-center gap-2 mt-2">
								<label class="form-label small text-body-secondary m-0 text-nowrap" for="previewTextInput">{m.voice_manager_preview_text_label()}</label>
								<div class="vm-preview-field">
									<input
										type="text"
										id="previewTextInput"
										class="form-control form-control-sm"
										maxlength="100"
										bind:value={previewText} />
									<span class="vm-preview-count" aria-live="polite">{previewText.length}/100</span>
								</div>
							</div>
						</div>

						<div class="vm-results" role="region" aria-labelledby="voiceBrowserTitle">
							{#if source === ELEVENLABS_SOURCE}
								<div class="vm-note mb-3">
									<i class="bi bi-info-circle" aria-hidden="true"></i>
									<span>{m.voice_manager_elevenlabs_multilingual_note()} {m.voice_manager_elevenlabs_model_hint()}</span>
									{#if elevenLabs.status === "ok"}
										<button type="button" class="btn btn-link btn-sm ms-auto text-nowrap" onclick={loadElevenLabsVoices}>
											<i class="bi bi-arrow-clockwise" aria-hidden="true"></i> {m.voice_manager_refresh()}
										</button>
									{/if}
								</div>

								{#if elevenLabs.status === "loading"}
									<div class="vm-empty">
										<Circle size="28" color="#1eb4b2" unit="px" duration="1s" />
									</div>
								{:else if elevenLabs.status === "no_key"}
									<div class="vm-empty" id="elevenLabsConnect">
										<i class="bi bi-plug vm-empty-icon" aria-hidden="true"></i>
										<h5>{m.voice_manager_elevenlabs_connect_title()}</h5>
										<p class="text-body-secondary">{m.voice_manager_elevenlabs_connect_desc()}</p>
										{#if canOpenIntegrations}
											<button type="button" id="connectElevenLabsBtn" class="btn btn-primary" onclick={openIntegrations}>
												{m.voice_manager_elevenlabs_connect_button()}
											</button>
										{:else}
											<p class="small text-body-secondary" id="elevenLabsPlanNote">{m.voice_manager_elevenlabs_plan_required()}</p>
										{/if}
									</div>
								{:else if elevenLabs.status === "invalid_key" || elevenLabs.status === "error"}
									<div class="alert alert-warning d-flex align-items-center gap-3" role="alert">
										<span class="me-auto">
											{elevenLabs.status === "invalid_key"
												? m.voice_manager_elevenlabs_invalid_key()
												: m.voice_manager_elevenlabs_error({ error: elevenLabs.message })}
										</span>
										<button type="button" class="btn btn-sm btn-outline-secondary text-nowrap" onclick={loadElevenLabsVoices}>{m.voice_manager_refresh()}</button>
										{#if canOpenIntegrations}
											<button type="button" class="btn btn-sm btn-primary text-nowrap" onclick={openIntegrations}>{m.voice_manager_elevenlabs_manage_button()}</button>
										{/if}
									</div>
								{:else if elevenLabs.status === "offline"}
									<div class="vm-empty"><p class="text-body-secondary">{m.voice_manager_elevenlabs_offline()}</p></div>
								{:else if elevenLabs.voices.length === 0}
									<div class="vm-empty"><p class="text-body-secondary">{m.voice_manager_elevenlabs_empty()}</p></div>
								{/if}
							{:else if source === OFFLINE_SOURCE && hasLowQualityOfflineVoices}
								<div class="alert alert-warning small" role="alert">
									{m.voice_manager_offline_low_quality_warning()}
								</div>
							{/if}

							{#each voiceGroups as group (group.id)}
								{#if source === ALL_CLOUD_SOURCE}
									<h6 class="vm-group-title">
										<i class="bi {sourceInfo[group.id].icon}" aria-hidden="true"></i> {sourceInfo[group.id].label()}
										<span class="text-body-secondary fw-normal">({group.voices.length})</span>
									</h6>
								{/if}
								<div class="vm-grid">
									{#each group.voices as voice (voiceKey(voice))}
										{@const pinned = isPinned(voice)}
										{@const tier = voiceTier(voice)}
										{@const locale = voiceLocale(voice)}
										{@const badges = [
											...(voice.provider === ELEVENLABS_SOURCE ? elevenLabsDetails(voice).map((detail) => ({ text: detail, cls: "vm-badge-muted text-capitalize" })) : []),
											...(voice.provider !== ELEVENLABS_SOURCE && voice.styles.length > 3 ? [{ text: m.voice_manager_styles_count_badge({ count: voice.styles.length }), cls: "vm-badge-muted", title: voice.styles.join(", ") }] : []),
											...(voice.provider === LOCAL_TTS_PROVIDER && parentDefaultVoiceId && voice.id === parentDefaultVoiceId ? [{ text: "Default", cls: "text-bg-primary" }] : []),
											...(voice.provider === WEB_SPEECH_PROVIDER && isLowQualityVoice(voice) ? [{ text: m.voice_manager_low_quality_badge(), cls: "text-bg-warning" }] : []),
											...(voice.provider === WEB_SPEECH_PROVIDER && isNetworkDependentVoice(voice) ? [{ text: m.voice_manager_cloud_voice_badge(), cls: "text-bg-info" }] : []),
										]}
										<div class="vm-card" class:vm-card-pinned={pinned}>
											<span class="vm-card-avatar" aria-hidden="true">
												<i class="bi {voice.gender === 'female' ? 'bi-person-standing-dress' : 'bi-person-standing'}"></i>
											</span>
											<div class="vm-card-body">
												<div class="vm-card-name" title={voice.name}>{displayName(voice)}</div>
												<div class="vm-card-meta">
													<span class="vm-card-meta-text">{voiceMeta(voice, tier, locale)}</span>
													{#each badges as badge (badge.text)}
														<span class="badge rounded-pill {badge.cls}" title={badge.title}>{badge.text}</span>
													{/each}
												</div>
											</div>
											<div class="vm-card-actions">
												<button
													type="button"
													class="vm-icon-btn"
													title={m.voice_manager_preview_label({ name: voice.name })}
													aria-label={m.voice_manager_preview_label({ name: voice.name })}
													disabled={rendering && voiceKey(selectedVoice) !== voiceKey(voice)}
													onclick={() => previewVoice(voice)}>
													{#if rendering && voiceKey(selectedVoice) === voiceKey(voice)}
														<Circle size="14" color="currentColor" unit="px" duration="1s" />
													{:else}
														<i class="bi bi-play-fill" aria-hidden="true"></i>
													{/if}
												</button>
												<button
													type="button"
													class="vm-icon-btn"
													class:vm-icon-btn-active={pinned}
													title={pinned ? m.voice_manager_unpin_title() : m.voice_manager_pin_title()}
													aria-label={pinned ? m.voice_manager_unpin_label({ name: voice.name }) : m.voice_manager_pin_label({ name: voice.name })}
													aria-pressed={pinned}
													onclick={() => togglePin(voice)}>
													<i class="bi {pinned ? 'bi-pin-angle-fill' : 'bi-pin-angle'}" aria-hidden="true"></i>
												</button>
											</div>
										</div>
									{/each}
								</div>
							{/each}

							{#if visibleVoices.length === 0 && sourceVoices.length > 0}
								<div class="vm-empty"><p class="text-body-secondary">{m.voice_manager_no_results()}</p></div>
							{/if}
						</div>
					</section>

					<!-- Pinned -->
					<aside class="vm-pinned border-start" aria-labelledby="pinnedVoicesSection">
						<p class="vm-section-label" id="pinnedVoicesSection">{m.voice_manager_pinned_voices_label()} <span class="text-body-secondary">({voiceState.length})</span></p>
						{#if voiceState.length === 0}
							<p class="small text-body-secondary px-1">{m.voice_manager_pinned_empty()}</p>
						{/if}
						<ul class="list-unstyled m-0">
							{#each voiceState as pinnedVoice (voiceKey(pinnedVoice))}
								<li class="vm-pin">
									<div class="min-w-0 flex-grow-1">
										<div class="text-truncate" title={pinnedVoice.name}>{displayName(pinnedVoice)}</div>
										<div class="small text-body-secondary text-truncate">
											{providerLabel(pinnedVoice.provider)}{voiceTier(pinnedVoice) ? ` · ${voiceTier(pinnedVoice)}` : ""}
										</div>
									</div>
									<button
										type="button"
										class="vm-icon-btn vm-pin-remove"
										aria-label={m.voice_manager_unpin_label({ name: pinnedVoice.name })}
										title={m.voice_manager_unpin_title()}
										onclick={() => unPinVoice(pinnedVoice)}>
										<i class="bi bi-x-lg" aria-hidden="true"></i>
									</button>
								</li>
							{/each}
						</ul>
					</aside>
				</div>
			</div>
		</div>
	</div>
</div>

<style lang="css">
	/* The side rails paint their own background; clip them so the bottom corners stay rounded */
	.modal-body {
		overflow: hidden;
		border-bottom-left-radius: var(--bs-modal-inner-border-radius);
		border-bottom-right-radius: var(--bs-modal-inner-border-radius);
	}

	.vm-layout {
		display: grid;
		grid-template-columns: 210px minmax(0, 1fr) 220px;
		height: min(72vh, 720px);
	}

	.vm-sources,
	.vm-pinned {
		padding: 0.75rem;
		overflow-y: auto;
		background-color: var(--bs-tertiary-bg);
	}

	.vm-section-label {
		font-size: 0.75rem;
		font-weight: 600;
		letter-spacing: 0.04em;
		text-transform: uppercase;
		color: var(--bs-secondary-color);
		margin: 0 0 0.5rem 0.25rem;
	}

	.vm-source {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		width: 100%;
		padding: 0.45rem 0.6rem;
		border: 0;
		border-radius: var(--bs-border-radius);
		background: transparent;
		color: var(--bs-body-color);
		text-align: left;
		font-size: 0.9rem;
	}

	.vm-source-child {
		padding-left: 1.25rem;
	}

	.vm-source:hover:not(:disabled) {
		background-color: var(--bs-secondary-bg);
	}

	.vm-source.active {
		background-color: var(--bs-primary-bg-subtle);
		color: var(--bs-primary-text-emphasis);
		font-weight: 600;
	}

	.vm-source:disabled {
		opacity: 0.5;
	}

	.vm-source-name {
		flex-grow: 1;
		min-width: 0;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.vm-source-count {
		font-size: 0.75rem;
		color: var(--bs-secondary-color);
	}

	.vm-source.active .vm-source-count {
		color: inherit;
		opacity: 0.8;
	}

	.vm-browser {
		display: flex;
		flex-direction: column;
		min-width: 0;
		min-height: 0;
	}

	.vm-toolbar {
		padding: 0.75rem 1rem;
	}

	.vm-search {
		position: relative;
		width: 220px;
	}

	.vm-search-icon {
		position: absolute;
		left: 0.6rem;
		top: 50%;
		transform: translateY(-50%);
		font-size: 0.8rem;
		color: var(--bs-secondary-color);
		pointer-events: none;
	}

	.vm-search .form-control {
		padding-left: 1.8rem;
	}

	.vm-language {
		width: 190px;
	}

	/* Segmented gender filter: light pills instead of solid buttons */
	.vm-segmented {
		display: inline-flex;
		gap: 0.125rem;
		padding: 0.125rem;
		border-radius: var(--bs-border-radius);
		background-color: var(--bs-tertiary-bg);
	}

	.vm-segment {
		padding: 0.15rem 0.6rem;
		border: 0;
		border-radius: calc(var(--bs-border-radius) - 1px);
		background: transparent;
		color: var(--bs-secondary-color);
		font-size: 0.8rem;
		line-height: 1.5;
	}

	.vm-segment:hover {
		color: var(--bs-body-color);
	}

	.vm-segment.active {
		background-color: var(--bs-body-bg);
		color: var(--bs-body-color);
		font-weight: 600;
		box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
	}

	.vm-preview-field {
		position: relative;
		flex-grow: 1;
		min-width: 0;
	}

	.vm-preview-field .form-control {
		padding-right: 3.5rem;
	}

	.vm-preview-count {
		position: absolute;
		right: 0.6rem;
		top: 50%;
		transform: translateY(-50%);
		font-size: 0.75rem;
		color: var(--bs-secondary-color);
		pointer-events: none;
	}

	.vm-results {
		flex-grow: 1;
		overflow-y: auto;
		padding: 1rem;
	}

	.vm-note {
		display: flex;
		align-items: center;
		gap: 0.5rem;
		padding: 0.5rem 0.75rem;
		border-radius: var(--bs-border-radius);
		background-color: var(--bs-info-bg-subtle);
		color: var(--bs-info-text-emphasis);
		font-size: 0.875rem;
	}

	.vm-group-title {
		margin: 0.5rem 0 0.75rem;
	}

	.vm-group-title:not(:first-of-type) {
		margin-top: 1.5rem;
	}

	.vm-grid {
		display: grid;
		grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
		gap: 0.5rem;
	}

	/* Compact single-row voice card: avatar | name + meta | actions */
	.vm-card {
		display: flex;
		align-items: center;
		gap: 0.6rem;
		padding: 0.5rem 0.75rem;
		border: 1px solid var(--bs-border-color);
		border-radius: var(--bs-border-radius-lg);
		background-color: var(--bs-body-bg);
		transition: background-color 0.1s ease, border-color 0.1s ease;
	}

	.vm-card:hover,
	.vm-card:focus-within {
		background-color: var(--bs-tertiary-bg);
	}

	.vm-card-pinned {
		border-color: var(--bs-primary);
		background-color: var(--bs-primary-bg-subtle);
		color: var(--bs-primary-text-emphasis);
	}

	.vm-card-pinned:hover,
	.vm-card-pinned:focus-within {
		background-color: var(--bs-primary-bg-subtle);
	}

	.vm-card-pinned .vm-card-meta,
	.vm-card-pinned .vm-icon-btn {
		color: var(--bs-primary-text-emphasis);
	}

	.vm-card-pinned .vm-card-meta {
		opacity: 0.8;
	}

	.vm-card-pinned .vm-icon-btn:hover:not(:disabled),
	.vm-card-pinned .vm-icon-btn:focus-visible {
		background-color: rgba(0, 0, 0, 0.12);
		color: var(--bs-primary-text-emphasis);
	}

	.vm-card-avatar {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;
		width: 2rem;
		height: 2rem;
		border-radius: 50%;
		background-color: var(--bs-secondary-bg);
		color: var(--bs-secondary-color);
		font-size: 1.1rem;
		line-height: 1;
	}

	.vm-card-pinned .vm-card-avatar {
		background-color: rgba(0, 0, 0, 0.12);
		color: var(--bs-primary-text-emphasis);
	}

	.vm-card-body {
		flex-grow: 1;
		min-width: 0;
	}

	.vm-card-name {
		font-weight: 600;
		font-size: 0.9rem;
		line-height: 1.3;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.vm-card-meta {
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: 0.25rem 0.4rem;
		font-size: 0.75rem;
		line-height: 1.3;
		color: var(--bs-secondary-color);
	}

	.vm-card-meta-text {
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.vm-card-meta .badge {
		font-size: 0.65rem;
		font-weight: 500;
		padding: 0.15em 0.5em;
	}

	.vm-badge-muted {
		background-color: var(--bs-secondary-bg);
		color: var(--bs-secondary-color);
	}

	.vm-card-actions {
		display: flex;
		flex-shrink: 0;
		gap: 0.125rem;
		opacity: 0.55;
		transition: opacity 0.1s ease;
	}

	.vm-card:hover .vm-card-actions,
	.vm-card:focus-within .vm-card-actions,
	.vm-card-pinned .vm-card-actions {
		opacity: 1;
	}

	/* Borderless icon button shared by the card actions and pinned rail */
	.vm-icon-btn {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 1.9rem;
		height: 1.9rem;
		padding: 0;
		border: 0;
		border-radius: 50%;
		background: transparent;
		color: var(--bs-secondary-color);
		font-size: 1rem;
		line-height: 1;
	}

	.vm-icon-btn:hover:not(:disabled),
	.vm-icon-btn:focus-visible {
		background-color: var(--bs-secondary-bg);
		color: var(--bs-body-color);
	}

	.vm-icon-btn:focus-visible {
		outline: 2px solid var(--bs-primary);
		outline-offset: 1px;
	}

	.vm-icon-btn:disabled {
		opacity: 0.4;
	}

	.vm-icon-btn-active {
		color: var(--bs-primary);
	}

	.vm-icon-btn-active:hover:not(:disabled) {
		color: var(--bs-primary);
	}

	.vm-empty {
		display: flex;
		flex-direction: column;
		align-items: center;
		justify-content: center;
		text-align: center;
		padding: 3rem 1rem;
	}

	.vm-empty-icon {
		font-size: 2.5rem;
		color: var(--bs-secondary-color);
		margin-bottom: 0.5rem;
	}

	.vm-pin {
		display: flex;
		align-items: center;
		gap: 0.25rem;
		padding: 0.4rem 0.25rem 0.4rem 0.5rem;
		border-radius: var(--bs-border-radius);
		font-size: 0.875rem;
	}

	.vm-pin:hover,
	.vm-pin:focus-within {
		background-color: var(--bs-secondary-bg);
	}

	.vm-pin-remove {
		width: 1.6rem;
		height: 1.6rem;
		font-size: 0.75rem;
		opacity: 0;
	}

	.vm-pin:hover .vm-pin-remove,
	.vm-pin:focus-within .vm-pin-remove {
		opacity: 1;
	}

	.vm-pin-remove:hover:not(:disabled) {
		background-color: var(--bs-body-bg);
	}

	.min-w-0 {
		min-width: 0;
	}
</style>
